import React, { useState } from 'react';
import { LoccitaneClose } from '../Icons/LoccitaneClose';
import { Box, Button, Image, Text } from '@chakra-ui/react';
import StarryBG from '../assets/images/StarryBG.jpeg';
import { Answer, ProductData, ProductVariant, Question, QuizProps } from '../interfaces/components';
import AnswerBox from '../components/Quiz/AnswerBox';
import { Eclipse } from '../Icons/Eclipse';
import { LeftArrow } from '../Icons/LeftArrow';
import axios from 'axios';
import useWindowDimensions from '../utils/hooks/useWindowDimensions';
import Spinner from '../components/common/Spinner';
import { questions, QuizContent } from '../content';
import { dispatchAddToCartAnalytics } from '../Analytics';

function Quiz({ activeLocale, active, setActive, setToastActive, openProduct }: QuizProps) {
  const { width } = useWindowDimensions();
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [fetchedProducts, setFetchedProducts] = useState<ProductData[]>([]);
  const [showProducts, setShowProducts] = useState(false);
  const [loading, setLoading] = useState(false);

  const addToCart = (title: string) => {
    const filteredProduct = fetchedProducts.filter((fetchedProduct: ProductData) => fetchedProduct.title === title);
    const product = filteredProduct[0];
    const productVariant = product?.variants.find((variant: ProductVariant) => variant.default);

    const analyticItem = {
      item_id: productVariant?.variant_id ? productVariant?.variant_id : '',
      item_name: product?.title ? product?.title : '',
      affiliation: '',
      coupon: '',
      discount: 0,
      currency: product?.currency ? product?.currency : '',
      index: 0,
      item_brand: "L'Occitane",
      item_category: productVariant?.variants[0]?.value ? productVariant?.variants[0]?.value : '',
      item_category2: product?.category ? product?.category : '',
      item_category3: product?.collection ? product.collection : '',
      item_category4: '',
      item_category5: productVariant?.retail_price ? productVariant?.retail_price : '', // should also be price
      item_list_id: '',
      item_list_name: '',
      item_variant: productVariant?.variant_id ? productVariant?.variant_id : '',
      location_id: '',
      price: parseInt(productVariant?.retail_price ? productVariant?.retail_price : ''),
      quantity: 1,
    };

    const eventMessage = {
      event_id: 'add_to_cart',
      product_id: productVariant?.variant_id ? productVariant?.variant_id : '',
      color_id: '',
      title: product?.title ? product?.title : '',
      price: product?.base_price,
      size_mpn: '',
      gtin: '',
    };
    console.log(eventMessage);
    window.parent.postMessage(eventMessage, '*');

    dispatchAddToCartAnalytics(
      product?.currency ? product?.currency : 'AED',
      productVariant?.retail_price ? productVariant?.retail_price : '',
      [analyticItem],
      'loccitane',
    );

    if (activeLocale === 'ar') {
      setToastActive('تم إضافة المنتج إلى سلة التسوق');
    } else {
      setToastActive('Product Added to Cart');
    }
  };

  const addQuizProductsToCart = () => {
    for (const product of fetchedProducts) {
      const productVariant = product?.variants.find((variant: ProductVariant) => variant.default);
      const analyticItem = {
        item_id: productVariant?.variant_id ? productVariant?.variant_id : '',
        item_name: product?.title ? product?.title : '',
        affiliation: '',
        coupon: '',
        discount: 0,
        currency: product?.currency ? product?.currency : '',
        index: 0,
        item_brand: "L'Occitane",
        item_category: productVariant?.variants[0]?.value ? productVariant?.variants[0]?.value : '',
        item_category2: product?.category ? product?.category : '',
        item_category3: product?.collection ? product.collection : '',
        item_category4: '',
        item_category5: productVariant?.retail_price ? productVariant?.retail_price : '', // should also be price
        item_list_id: '',
        item_list_name: '',
        item_variant: productVariant?.variant_id ? productVariant?.variant_id : '',
        location_id: '',
        price: parseInt(productVariant?.retail_price ? productVariant?.retail_price : ''),
        quantity: 1,
      };

      const eventMessage = {
        event_id: 'add_to_cart',
        product_id: productVariant?.variant_id ? productVariant?.variant_id : '',
        color_id: '',
        title: product?.title ? product?.title : '',
        price: product?.base_price,
        size_mpn: '',
        gtin: '',
      };
      console.log(eventMessage);
      window.parent.postMessage(eventMessage, '*');

      dispatchAddToCartAnalytics(
        product?.currency ? product?.currency : 'AED',
        productVariant?.retail_price ? productVariant?.retail_price : '',
        [analyticItem],
        'loccitane',
      );
    }

    if (activeLocale === 'ar') {
      setToastActive('المنتجات المضافة إلى سلة التسوق');
    } else {
      setToastActive('Products Added to Cart');
    }
  };

  const fetchProductById = async (productId: string, apiLocale: string) => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + `${apiLocale}/` + productId, {
        params: {
          'include-data': false,
        },
      });
      return response.data;
    } catch (error: unknown) {
      console.error(`Error fetching product with ID ${productId}`);
      throw error;
    }
  };

  const fetchProducts = async (productIds: string[], apiLocale: string) => {
    const products = [];

    for (const productId of productIds) {
      try {
        const product = await fetchProductById(productId, apiLocale);
        products.push(product.data);
      } catch (error) {
        console.log('Error in fetchProducts');
      }
    }
    return products;
  };

  const updateProductID = (pId: string, index: number) => {
    const updatedProductIds = productIds;

    if (updatedProductIds[index]) {
      updatedProductIds[index] = pId;
    } else {
      updatedProductIds.push(pId);
    }

    setProductIds(updatedProductIds);

    console.log(updatedProductIds);
    console.log(updatedProductIds.length);

    if (updatedProductIds.length === 4) {
      setLoading(true);
      setShowProducts(true);

      let apiLocale = '';

      if (window.locale === 'ar-ae') {
        apiLocale = 'ar-ae';
      } else if (window.locale === 'ar-sa') {
        apiLocale = 'ar-sa';
      } else if (window.locale === 'en-sa') {
        apiLocale = 'en-sa';
      } else if (window.locale === 'en-kw') {
        apiLocale = 'en-kw';
      } else if (window.locale === 'ar-kw') {
        apiLocale = 'ar-kw';
      } else if (window.locale === 'en-qa') {
        apiLocale = 'en-qa';
      } else if (window.locale === 'ar-qa') {
        apiLocale = 'ar-qa';
      } else {
        apiLocale = 'en-ae';
      }

      fetchProducts(updatedProductIds, apiLocale)
        .then((products) => {
          setLoading(false);
          console.log(products);
          setFetchedProducts(products);
        })
        .catch((error) => {
          console.error('Error fetching products:', error);
        });
    }
  };

  const retakeQuiz = () => {
    setShowProducts(false);
    setProductIds([]);
    setFetchedProducts([]);
    setActiveQuestion(0);
  };

  return (
    <>
      <Box
        position="fixed"
        w="100%"
        h="100%"
        top="0"
        left="0"
        opacity={active ? 1 : 0}
        visibility={active ? 'visible' : 'hidden'}
        backgroundColor="rgba(0,0,0,0.5)"
        transition="all 1s"
        zIndex="9"
        onClick={() => {
          retakeQuiz();
          setActive();
        }}
      />

      <Box
        zIndex="10"
        opacity={active ? 1 : 0}
        visibility={active ? 'visible' : 'hidden'}
        transition="all 0.5s"
        position="fixed"
        top={0}
        right={0}
        left={0}
        bottom={0}
        margin="auto"
        bg="white"
        width={
          showProducts ? ['90vw', '360px', '450px', '850px', '1000px'] : ['90vw', '360px', '400px', '750px', '800px']
        }
        height={
          showProducts ? ['500px', '550px', '600px', '550px', '650px'] : ['500px', '500px', '550px', '450px', '500px']
        }
      >
        <Image
          position="absolute"
          height="100%"
          w="100%"
          src={StarryBG}
          objectFit="cover"
          objectPosition="top"
          zIndex="10"
        />

        <LoccitaneClose
          boxSize={[6, 6, 8]}
          position="absolute"
          top={['10px', '10px', '15px', '15px']}
          right={['10px', '10px', '15px', '15px']}
          cursor="pointer"
          fill="black"
          strokeWidth={0.5}
          onClick={() => {
            retakeQuiz();
            setActive();
          }}
          zIndex="30"
          stroke="#CCCCCC"
          borderRadius="100%"
          _hover={{ stroke: 'black' }}
          transition=".5s"
        />

        <Box
          opacity={!loading && !showProducts && active ? 1 : 0}
          visibility={!loading && !showProducts && active ? 'visible' : 'hidden'}
          transition="all 0.5s"
          width="100%"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="center"
          height={['260px', '260px', '280px', '230px']}
          position="absolute"
          zIndex="40"
          margin="auto"
          top="0"
          bottom="0"
        >
          <Box>
            <Text
              color="white"
              fontSize={['13px', '13px', ' 14px', '15px']}
              textAlign="center"
              fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
            >
              {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                ? QuizContent['en'].top
                : QuizContent[activeLocale as keyof typeof QuizContent].top}
            </Text>
            <Text
              display="flex"
              justifyContent="center"
              alignItems="center"
              color="white"
              mt={['5px', '5px', '0px']}
              height={['46px', '46px', '50px', '70px']}
              lineHeight={['23px', '23px', '25px', '35px']}
              fontSize={['18px', '18px', '20px', '30px']}
              maxW={['250px', '250px', '290px', '530px']}
              textAlign="center"
              fontFamily={activeLocale === 'ar' ? 'GETwoLight' : 'LoccitaneRegular'}
            >
              {activeLocale === 'ar'
                ? questions[activeQuestion].questionAR
                : `${activeQuestion + 1}. ` + questions[activeQuestion].question}
            </Text>
          </Box>

          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            maxW={['220px', '240px', '290px', '550px']}
            width="auto"
          >
            {activeLocale === 'ar'
              ? questions[activeQuestion].answersAR.map((answer: Answer) => (
                  <AnswerBox
                    key={answer.text}
                    activeLocale={activeLocale}
                    text={answer.text}
                    onClick={() => {
                      updateProductID(answer.productId, questions[activeQuestion].index);
                      if (activeQuestion <= 2) {
                        setActiveQuestion((count) => count + 1);
                      }
                    }}
                  />
                ))
              : questions[activeQuestion].answers.map((answer: Answer) => (
                  <AnswerBox
                    key={answer.text}
                    activeLocale={activeLocale}
                    text={answer.text}
                    onClick={() => {
                      updateProductID(answer.productId, questions[activeQuestion].index);
                      if (activeQuestion <= 2) {
                        setActiveQuestion((count) => count + 1);
                      }
                    }}
                  />
                ))}
          </Box>

          <Box>
            {questions.map((ques: Question, index: number) => (
              <Eclipse
                borderRadius="100%"
                key={ques.question}
                m="0px 5px"
                boxSize={[2, 2, 3]}
                strokeWidth="2"
                stroke={activeQuestion === index ? '#FFD220' : '#D9D9D9'}
                fill={activeQuestion === index ? '#FFD220' : 'transparent'}
              />
            ))}
          </Box>
        </Box>

        {loading ? (
          <Box position="absolute" zIndex="50">
            <Spinner />
          </Box>
        ) : (
          <Box
            opacity={showProducts && active ? 1 : 0}
            visibility={showProducts && active ? 'visible' : 'hidden'}
            transition="all 0.5s"
            width="100%"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            alignItems="center"
            height={['420px', '470px', '500px', '480px', '550px']}
            position="absolute"
            zIndex="40"
            margin="auto"
            top="0"
            bottom="0"
          >
            <Box>
              <Text
                color="white"
                fontSize={['13px', '13px', ' 14px', '15px']}
                textAlign="center"
                fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
              >
                {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                  ? QuizContent['en'].result
                  : QuizContent[activeLocale as keyof typeof QuizContent].result}
              </Text>
              <Text
                display="flex"
                justifyContent="center"
                alignItems="center"
                color="white"
                mt={['5px', '5px', '0px']}
                height={['46px', '46px', '50px', '70px']}
                lineHeight={['23px', '23px', '25px', '35px']}
                fontSize={['18px', '18px', '20px', '30px']}
                maxW={['250px', '250px', '290px', '530px']}
                textAlign="center"
                fontFamily={activeLocale === 'ar' ? 'GETwoLight' : 'LoccitaneRegular'}
              >
                {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                  ? QuizContent['en'].text
                  : QuizContent[activeLocale as keyof typeof QuizContent].text}
              </Text>
            </Box>

            <Box
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              width={['230px', '270px', '300px', '750px', '900px']}
              maxH={['260px', '290px', '320px', '900px', '900px']}
              overflow="auto"
            >
              {fetchedProducts.map((product: ProductData, index: number) =>
                width < 999 ? (
                  <Box
                    _notLast={{ marginBottom: '10px' }}
                    height={['75px', '85px', '85px', '85px']}
                    width={['100%']}
                    p={['5px']}
                    key={product.title + index}
                    bg="white"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Image
                      w={['60px', '70px', '70px', '70px']}
                      h={['65px', '75px', '75px', '75px']}
                      objectFit="contain"
                      src={product?.variants[0]?.media[0]?.url}
                    />

                    <Box
                      w={['155px', '185px', '200px', '155px']}
                      h={['65px', '65px', '65px', '65px']}
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box w="100%">
                        <Text
                          noOfLines={1}
                          maxW={['200px']}
                          textAlign="left"
                          fontSize={['12px', '13px', '14px', '15px', '14px']}
                          lineHeight={['13px', '14px', '15px', '15px', '14px']}
                          fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
                        >
                          {product.title}
                        </Text>
                        <Text
                          noOfLines={1}
                          maxW={['200px', '200px', '200px', '200px']}
                          textAlign="left"
                          fontSize={['10px', '11px', '12px', '15px', '13px']}
                          fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
                          fontWeight="900"
                        >
                          {parseInt(product.base_price) + ' ' + product.currency}
                        </Text>
                      </Box>

                      <Box w="100%" display="flex" justifyContent="space-between" alignItems="center">
                        <Box
                          bg="white"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          textTransform="uppercase"
                          fontFamily={activeLocale === 'ar' ? 'GETwoLight' : 'RalewayMedium'}
                          fontSize={['8px', '10px', '10px', '8px']}
                          fontWeight="900"
                          textAlign="center"
                          _hover={{ bg: 'lightgrey' }}
                          cursor="pointer"
                          transition="0.2s ease-in all"
                          border="1px solid black"
                          borderRadius="10px"
                          p={['2px 6px']}
                          onClick={() => {
                            const productVariant = product?.variants.find((variant: ProductVariant) => variant.default);
                            openProduct(productVariant?.variant_id || '');
                          }}
                        >
                          {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                            ? QuizContent['en'].learnMore
                            : QuizContent[activeLocale as keyof typeof QuizContent].learnMore}
                        </Box>
                        <Button
                          bg="#FFCB00"
                          borderRadius="0px"
                          w={['80px', '100px', '100px', '230px', '100%']}
                          height={['30px', '30px', '35px', '40px', '40px']}
                          fontSize={['10px', '12px', '13px', '15px', '15px']}
                          _hover={{ bg: 'rgb(255, 203, 0, 0.4)' }}
                          _focus={{ bg: '#FFCB00' }}
                          fontFamily={activeLocale === 'ar' ? 'GETwoLight' : 'RalewayMedium'}
                          textTransform="uppercase"
                          onClick={() => addToCart(product.title)}
                        >
                          {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                            ? QuizContent['en'].addToCart
                            : QuizContent[activeLocale as keyof typeof QuizContent].addToCart}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    height={['300px', '300px', '300px', '260px', '300px']}
                    width={['220px', '220px', '220px', '180px', '220px']}
                    key={product.title + index}
                    bg="white"
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    alignItems="center"
                    position="relative"
                  >
                    <Box
                      top="10px"
                      right="10px"
                      position="absolute"
                      bg="white"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      textTransform="uppercase"
                      fontFamily={activeLocale === 'ar' ? 'GETwoLight' : 'RalewayMedium'}
                      fontSize="10px"
                      fontWeight="900"
                      textAlign="center"
                      _hover={{ bg: 'lightgrey' }}
                      cursor="pointer"
                      transition="0.2s ease-in all"
                      border="1px solid black"
                      borderRadius="10px"
                      p={['2px 6px']}
                      onClick={() => {
                        const productVariant = product?.variants.find((variant: ProductVariant) => variant.default);
                        openProduct(productVariant?.variant_id || '');
                      }}
                    >
                      {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                        ? QuizContent['en'].learnMore
                        : QuizContent[activeLocale as keyof typeof QuizContent].learnMore}
                    </Box>
                    <Image
                      w={['100%']}
                      h={['200px', '200px', '200px', '160px', '200px']}
                      objectFit="contain"
                      src={product?.variants[0]?.media[0]?.url}
                    />

                    <Box>
                      <Text
                        noOfLines={1}
                        maxW={['200px', '200px', '200px', '160px', '200px']}
                        textAlign="center"
                        fontSize={['12px', '13px', '15px', '14px', '14px']}
                        fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
                      >
                        {product.title}
                      </Text>
                      <Text
                        noOfLines={1}
                        textAlign="center"
                        fontSize={['12px', '13px', '15px', '13px', '13px']}
                        fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
                        fontWeight="900"
                      >
                        {parseInt(product.base_price) + ' ' + product.currency}
                      </Text>
                    </Box>

                    <Button
                      bg="#FFCB00"
                      borderRadius="0px"
                      w={['auto', '180px', '230px', '100%', '100%']}
                      height={['35px', '35px', '40px', '40px', '40px']}
                      fontSize={['12px', '13px', '15px', '15px', '15px']}
                      _hover={{ bg: 'rgb(255, 203, 0, 0.4)' }}
                      _focus={{ bg: '#FFCB00' }}
                      fontFamily={activeLocale === 'ar' ? 'GETwoLight' : 'RalewayMedium'}
                      textTransform="uppercase"
                      onClick={() => addToCart(product.title)}
                    >
                      {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                        ? QuizContent['en'].addToCart
                        : QuizContent[activeLocale as keyof typeof QuizContent].addToCart}
                    </Button>
                  </Box>
                ),
              )}
            </Box>

            <Box>
              <Box
                bg="white"
                textTransform="uppercase"
                fontFamily={activeLocale === 'ar' ? 'GETwoLight' : 'RalewayMedium'}
                fontWeight="900"
                textAlign="center"
                fontSize={['10px', '12px', '12px', '15px', '15px']}
                p={['5px 10px', '10px 15px', '10px 15px']}
                _hover={{ bg: 'lightgrey' }}
                cursor="pointer"
                transition="0.2s ease-in all"
                onClick={() => addQuizProductsToCart()}
              >
                {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                  ? QuizContent['en'].shopAll
                  : QuizContent[activeLocale as keyof typeof QuizContent].shopAll}
              </Box>
              <Text
                mt={['10px']}
                color="#FFFFFFB2"
                fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
                fontSize={['10px', '12px', '12px', '14px', '14px']}
                textAlign="center"
                textDecoration="underline"
                cursor="pointer"
                onClick={retakeQuiz}
              >
                {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
                  ? QuizContent['en'].retake
                  : QuizContent[activeLocale as keyof typeof QuizContent].retake}
              </Text>
            </Box>
          </Box>
        )}

        <Box
          opacity={!showProducts && active && activeQuestion > 0 ? 1 : 0}
          visibility={!showProducts && active && activeQuestion > 0 ? 'visible' : 'hidden'}
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="absolute"
          zIndex="40"
          bottom={['10px', '10px', '15px', '15px']}
          left={['10px', '10px', '15px', '15px']}
          onClick={() => {
            activeQuestion > 0 && setActiveQuestion((count) => count - 1);
          }}
          cursor="pointer"
          transition="0.2s ease-in all"
        >
          <LeftArrow boxSize={[3, 4, 4, 4]} stroke="#FFFFFFB2" />
          <Text
            ml={['5px']}
            color="#FFFFFFB2"
            fontFamily={activeLocale.includes('ar') ? 'GETwoLight' : 'RalewayMedium'}
            fontSize={['13px', '13px', '15px', '15px']}
            textAlign="center"
          >
            {activeLocale === undefined || !((activeLocale as keyof typeof QuizContent) in QuizContent)
              ? QuizContent['en'].prev
              : QuizContent[activeLocale as keyof typeof QuizContent].prev}
          </Text>
        </Box>
      </Box>
    </>
  );
}

export default Quiz;
